import HelpCenterCard from '@/components/HelpCenterCard';
import SEOHead from '@/components/SEOHead';
import SocialShare from '@/components/SocialShare';
import { client } from '@/utils/sanity-client';
import { ClockCircleOutlined, ShareAltOutlined } from '@ant-design/icons';
import BlockContent from '@sanity/block-content-to-react';
import { Card, Col, Progress, Row, Skeleton, Space, Typography } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

const { Title, Text, Paragraph } = Typography;

const urlToTypeMap = {
  'case-studies': 'caseStudies',
  'industry-news': 'industryNews',
  articles: 'articles',
  faq: 'faq',
  guides: 'guides',
};

const typeToUrlMap = {
  caseStudies: 'case-studies',
  industryNews: 'industry-news',
  articles: 'articles',
  faq: 'faq',
  guides: 'guides',
};

const DocumentPage = () => {
  const [document, setDocument] = useState(null);
  const [relatedDocs, setRelatedDocs] = useState([]);
  const [readingProgress, setReadingProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const contentRef = useRef(null);
  const stickyRef = useRef(null);

  useEffect(() => {
    const { category, document: documentSlug } = router.query;
    if (category && documentSlug) {
      setLoading(true);
      // Map URL category to Sanity type
      const type = urlToTypeMap[category] || category;

      // Fetch main document
      client
        .fetch(
          `*[_type == $type && slug.current == $documentSlug][0] {
            _id,
            title,
            question,
            name,
            body,
            definition,
            answer,
            "slug": slug.current,
            _createdAt,
            "estimatedReadTime": length(pt::text(body)) / 1000
          }`,
          { type, documentSlug }
        )
        .then(async (data) => {
          if (data) {
            setDocument(data);
            // Fetch related documents
            const relatedQuery = `*[_type == $type && slug.current != $documentSlug][0...3] {
              _id,
              title,
              question,
              name,
              "slug": slug.current,
              "preview": pt::text(body[0...1]),
              body
            }`;
            const related = await client.fetch(relatedQuery, { type, documentSlug });
            setRelatedDocs(related);
          } else {
            router.push('/404');
          }
        })
        .catch((error) => {
          console.error(error);
          router.push('/404');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [router, router.query]);

  useEffect(() => {
    const calculateReadingProgress = () => {
      const content = contentRef.current;
      if (!content) return;

      // Get the content's position relative to the viewport
      const contentTop = content.getBoundingClientRect().top + window.scrollY;
      const contentHeight = content.scrollHeight;
      const windowHeight = window.innerHeight;
      const currentScroll = Math.max(0, window.scrollY - contentTop);

      // Adjust maxScroll to account for viewport height
      // This ensures 100% is reached when the bottom of the content enters the viewport
      const maxScroll = contentHeight - windowHeight;

      // Calculate progress percentage
      const progress = Math.min(100, Math.max(0, Math.round((currentScroll / maxScroll) * 100)));
      setReadingProgress(progress);
    };

    window.addEventListener('scroll', calculateReadingProgress);
    calculateReadingProgress(); // Initial calculation
    return () => window.removeEventListener('scroll', calculateReadingProgress);
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: 'calc(100vh - 100px)' }}>
        <section className="section hero v4" style={{ paddingTop: '132px', paddingBottom: '0' }}>
          <div className="container-default w-container">
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        </section>
        <div
          className="content-wrapper"
          style={{
            background: '#ffffff',
            minHeight: 'calc(100vh - 300px)',
            paddingBottom: '120px',
          }}
        >
          <div className="container-default w-container">
            <Row gutter={[48, 48]}>
              <Col xs={24} md={17}>
                <Card style={{ borderRadius: '12px' }}>
                  <Skeleton active paragraph={{ rows: 12 }} />
                </Card>
              </Col>
              <Col xs={24} md={7}>
                <Card style={{ borderRadius: '12px', marginBottom: '24px' }}>
                  <Skeleton active paragraph={{ rows: 1 }} />
                </Card>
                <Card style={{ borderRadius: '12px' }}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  }

  if (!document) return null;

  const categoryName = router.query.category
    ? router.query.category
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';
  const title = 'Brandymail.com - ' + (document.title || document.question || document.name);
  const fullUrl = `https://brandymail.com${router.asPath}`;

  return (
    <div style={{ position: 'relative' }}>
      <SEOHead document={document} />

      <section className="section hero v4" style={{ paddingTop: '132px', paddingBottom: '0' }}>
        <div className="container-default w-container">
          <div className="z-index-1">
            <div className="mg-bottom-24px">
              <div className="inner-container center">
                <div className="inner-container _861px---tablet center">
                  <div className="text-center">
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        <Link href="/help-center">Help Center</Link>
                        {' > '}
                        <Link href={`/${router.query.category}`}>{categoryName}</Link>
                      </div>
                    </div>
                    <h1 className="mg-bottom-16px">
                      {document.title || document.question || document.name}
                    </h1>
                    {document.estimatedReadTime && (
                      <Text className="color-neutral-600">
                        <ClockCircleOutlined /> {Math.ceil(document.estimatedReadTime)} min read
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="position-absolute card-top-bg---bottom pricing-bg" />
      </section>

      <div
        className="content-wrapper"
        style={{
          background: '#ffffff',
          minHeight: 'calc(100vh - 300px)',
          paddingBottom: '120px',
        }}
      >
        <div className="container-default w-container" style={{ position: 'relative' }}>
          <Row gutter={[48, 48]}>
            <Col xs={24} md={17}>
              <div ref={contentRef} style={{ position: 'relative' }}>
                <Card
                  className="content-card"
                  style={{
                    borderRadius: '12px',
                    background: '#fff',
                    padding: '40px',
                    marginBottom: '40px',
                  }}
                >
                  <div className="article-content">
                    {document?.body ? (
                      <BlockContent blocks={document.body} />
                    ) : document?.definition ? (
                      <BlockContent blocks={document.definition} />
                    ) : document?.answer ? (
                      <div>
                        <Title level={4} style={{ marginBottom: '24px' }}>
                          {document.question}
                        </Title>
                        <Paragraph>{document.answer}</Paragraph>
                      </div>
                    ) : (
                      <div>No content available</div>
                    )}
                  </div>
                </Card>

                {relatedDocs.length > 0 && (
                  <Card style={{ borderRadius: '12px', background: '#fff', padding: '40px' }}>
                    <Title level={3}>Related Content</Title>
                    <Row gutter={[24, 24]}>
                      {relatedDocs.map((doc) => (
                        <Col xs={24} sm={12} key={doc._id}>
                          <HelpCenterCard item={doc} type={router.query.category} />
                        </Col>
                      ))}
                    </Row>
                  </Card>
                )}
              </div>
            </Col>

            <Col xs={24} md={7}>
              <div
                ref={stickyRef}
                style={{
                  position: 'sticky',
                  top: '100px',
                  height: 'fit-content',
                  marginBottom: '40px',
                }}
              >
                <Card
                  className="progress-card"
                  style={{
                    borderRadius: '12px',
                    marginBottom: '24px',
                    background: '#ffffff',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Title level={5} style={{ margin: '0 0 12px 0' }}>
                      Reading Progress
                    </Title>
                    <Progress
                      percent={readingProgress}
                      status="active"
                      strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                      }}
                      strokeWidth={8}
                    />
                  </Space>
                </Card>

                <Card
                  className="share-card"
                  style={{
                    borderRadius: '12px',
                    background: '#ffffff',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Title level={5} style={{ margin: '0 0 12px 0' }}>
                      <ShareAltOutlined style={{ marginRight: '8px' }} /> Share {categoryName}
                    </Title>
                    <SocialShare url={fullUrl} title={title} />
                  </Space>
                </Card>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default DocumentPage;
